//Dependencies............................................
import { Tabs, Dropdown, Button, Modal, Input, InputNumber, Select, Tooltip, Space, Collapse } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';

//Components..............................................
import DeleteModal from "../../../../GlobalComponents/Modals/DeleteModal";
import { Path, childArrayKey, setAtPath, deleteAtPath } from "../../../../../utils/conditionTree";

const conditionItems = [
    { label: 'AND', key: 'and' },
    { label: 'OR', key: 'or' },
    { label: 'Compare', key: 'signal_compare' },
];

const operatorOptions = [
    { label: '==', value: 'eq' },
    { label: '!=', value: 'ne' },
    { label: '>', value: 'gt' },
    { label: '<', value: 'lt' },
    { label: '>=', value: 'gte' },
    { label: '<=', value: 'lte' },
];

// Same and/or/not tree editor as FunctionalityControl's RecursiveDropdown
// (see utils/conditionTree.ts for the shared Path/setAtPath/deleteAtPath
// helpers), except the leaf here is a signal comparison (signal + operator +
// value) instead of a reference to another rule - a LIN rule's `condition`
// combines multiple signal comparisons the same way functionality_control
// combines rule references.
function RecursiveSignalDropdown({
    depth,
    path,
    condition,
    linSignals,
    root,
    onChange,
    }   :   {
    depth: number,
    path: Path,
    condition: any,
    linSignals: any[],
    root: any,
    onChange: (newRoot: any) => void,
    })
{
    const selectedKey: 'and' | 'or' | 'signal_compare' | null =
        'and' in condition ? 'and' : 'or' in condition ? 'or' : ('signal_compare' in condition ? 'signal_compare' : null);

    const children: any[] = condition[selectedKey === 'and' ? 'and' : 'or'] || [];
    const compare = condition.signal_compare || {};
    const selectedSignal = compare.signal !== undefined && compare.signal !== null ? linSignals[compare.signal] : null;
    // 2**64-1 matches car-config.schema.json's signal_compare.value maximum
    // (and the firmware's uint64_t comparison value) - used before a signal
    // is picked, when there's no bit_length yet to derive a tighter bound.
    const maxValue = selectedSignal ? Math.pow(2, selectedSignal.bit_length) - 1 : Number.MAX_SAFE_INTEGER;

    const handleSelect = ({ key }: { key: string }) => {
        if (key === 'and' || key === 'or') {
            onChange(setAtPath(root, path, () => ({ [key]: [{}, {}] })));
        } else if (key === 'signal_compare') {
            // signal: null means "leaf type chosen, no specific signal
            // picked yet" - same "incomplete but valid shape" pattern used
            // for an unpicked rule reference in FunctionalityControl.
            onChange(setAtPath(root, path, () => ({ signal_compare: { signal: null, operator: 'eq', value: 0 } })));
        }
    };

    const updateCompare = (patch: any) => {
        onChange(setAtPath(root, path, (node: any) => ({
            signal_compare: { ...node.signal_compare, ...patch },
        })));
    };

    const handleAddChild = () => {
        onChange(setAtPath(root, path, (node: any) => {
            const key = childArrayKey(node);
            return { ...node, [key]: [...node[key], {}] };
        }));
    };

    const handleAddSibling = () => {
        onChange(setAtPath(root, path.slice(0, -1), (node: any) => {
            const key = childArrayKey(node);
            return { ...node, [key]: [...node[key], {}] };
        }));
    };

    const handleDeleteSelf = () => {
        onChange(deleteAtPath(root, path));
    };

    const getButtonType = (key: string | null) => {
        switch (key) {
            case 'and': return 'blue';
            case 'or': return 'cyan';
            case 'signal_compare': return 'pink';
        }
    };

    return (
        <div style={{ marginLeft: depth * 20 }}>
            <Dropdown
                menu={{ items: conditionItems, onClick: handleSelect }}
                trigger={['click']}
            >
                <Button color={getButtonType(selectedKey)} variant='outlined'>
                    {selectedKey === 'signal_compare' ? 'COMPARE' : selectedKey ? selectedKey.toUpperCase() : 'Select'}
                </Button>
            </Dropdown>
            {depth > 0 && (
                <Button onClick={handleDeleteSelf} color="red" variant='outlined'>-</Button>
            )}
            {depth > 0 && (
                <Button onClick={handleAddSibling} color="green" variant='outlined'>+</Button>
            )}
            {selectedKey === 'signal_compare' && (
                <Space style={{ marginLeft: 8 }}>
                    <Select
                        placeholder="Signal"
                        style={{ width: 200 }}
                        value={compare.signal ?? undefined}
                        options={linSignals.map((s, i) => ({ label: s.name, value: i }))}
                        onChange={(value) => updateCompare({ signal: value })}
                    />
                    <Select
                        style={{ width: 110 }}
                        value={compare.operator ?? 'eq'}
                        options={operatorOptions}
                        onChange={(value) => updateCompare({ operator: value })}
                    />
                    <InputNumber
                        min={0}
                        max={maxValue}
                        value={compare.value ?? 0}
                        onChange={(value) => updateCompare({ value: value ?? 0 })}
                    />
                </Space>
            )}
            {(selectedKey === 'and' || selectedKey === 'or') && (
                <Button onClick={handleAddChild} color="green" variant='outlined'>+</Button>
            )}
            <div>
                {children.map((subCondition, index) => (
                    <RecursiveSignalDropdown
                        key={index}
                        depth={depth + 1}
                        path={[...path, index]}
                        condition={subCondition}
                        linSignals={linSignals}
                        root={root}
                        onChange={onChange}
                    />
                ))}
            </div>
        </div>
    );
}

// Edits only the LIN entries of the shared `rules[]` array (see Rules.tsx
// for the same filtered-to-full index mapping pattern used for CAN/LIN
// rules elsewhere) - CAN rules are untouched and keep going through
// Rules.tsx as before.
export default function Conf30LinRulesField({
    onChange,
    formData,
    formContext,
    }   :   {
    formData: any,
    onChange: (newFormData: any) => void,
    formContext: any,
    })
{
    const allRules: any[] = useMemo(() => (Array.isArray(formData) ? formData : []), [formData]);
    const indexedRules = useMemo(() => allRules
        .map((rule, originalIndex) => ({ rule, originalIndex }))
        .filter(({ rule }) => rule.type === 'LIN'),
        [allRules]);

    const linSignals: any[] = formContext?.lin_signals || [];

    const [activeKey, setActiveKey] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingLocalIndex, setEditingLocalIndex] = useState<number | null>(null);
    const [newTabLabel, setNewTabLabel] = useState<string>('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);

    useEffect(() => {
        const keys = indexedRules.map((_, i) => String(i));
        if (keys.length === 0) {
            setActiveKey(null);
        } else if (!activeKey || !keys.includes(activeKey)) {
            setActiveKey(keys[0]);
        }
    }, [formData]);// eslint-disable-line react-hooks/exhaustive-deps

    const updateRuleCondition = (localIndex: number, newCondition: any) => {
        const originalIndex = indexedRules[localIndex].originalIndex;
        const updatedFull = [...allRules];
        updatedFull[originalIndex] = { ...updatedFull[originalIndex], condition: newCondition };
        onChange(updatedFull);
    };

    const handleAddTab = () => {
        const newRule = { type: 'LIN', description: 'Rename tab!', condition: {} };
        onChange([...allRules, newRule]);
        setActiveKey(String(indexedRules.length));
    };

    const showRenameModal = (localIndex: number) => {
        setEditingLocalIndex(localIndex);
        setNewTabLabel(indexedRules[localIndex].rule.description || '');
        setIsEditing(true);
    };

    const handleRenameTab = () => {
        if (editingLocalIndex !== null && newTabLabel) {
            const originalIndex = indexedRules[editingLocalIndex].originalIndex;
            const updatedFull = [...allRules];
            updatedFull[originalIndex] = { ...updatedFull[originalIndex], description: newTabLabel };
            onChange(updatedFull);
        }
        setIsEditing(false);
        setEditingLocalIndex(null);
        setNewTabLabel('');
    };

    const items = indexedRules.map(({ rule }, localIndex) => ({
        key: String(localIndex),
        label: (
            <span>
                <Tooltip title="Rename Tab">
                    <EditOutlined
                        onClick={() => showRenameModal(localIndex)}
                        style={{ cursor: 'pointer', marginLeft: 5 }}
                    />
                </Tooltip>
                {'  '}{rule.description || 'Rename tab!'}
            </span>
        ),
        children: (
            <RecursiveSignalDropdown
                depth={0}
                path={[]}
                condition={rule.condition || {}}
                linSignals={linSignals}
                root={rule.condition || {}}
                onChange={(newCondition: any) => updateRuleCondition(localIndex, newCondition)}
            />
        ),
        forceRender: true,
    }));

    return (
        <Collapse>
            <Collapse.Panel header="LIN Rules" key="1">
                <Tabs
                    type="editable-card"
                    onEdit={(key, action) => {
                        if (action === 'add') {
                            handleAddTab();
                        } else if (action === 'remove') {
                            setSelectedTabIndex(Number(key));
                            setShowDeleteModal(true);
                        }
                    }}
                    activeKey={activeKey || undefined}
                    onChange={(key) => setActiveKey(key)}
                    addIcon="+"
                    items={items}
                />
                <Modal
                    title="Rename Tab"
                    open={isEditing}
                    onOk={handleRenameTab}
                    onCancel={() => {
                        setIsEditing(false);
                        setEditingLocalIndex(null);
                    }}
                >
                    <Input
                        value={newTabLabel}
                        onChange={(e) => setNewTabLabel(e.target.value)}
                        placeholder="Enter new tab name"
                    />
                </Modal>
                <DeleteModal
                    showModal={showDeleteModal}
                    onDelete={() => {
                        const originalIndexToRemove = indexedRules[selectedTabIndex!].originalIndex;
                        onChange(allRules.filter((_, i) => i !== originalIndexToRemove));
                        setShowDeleteModal(false);
                        setSelectedTabIndex(null);
                    }}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedTabIndex(null);
                    }}
                />
            </Collapse.Panel>
        </Collapse>
    );
}
